/**
 * @fileoverview Dashboard page route with lazy loading for performance
 * @module routes/_authenticated/dashboard
 *
 * PERFORMANCE: Dashboard is lazy-loaded to prevent blocking navigation.
 * Without lazy loading, navigating to /dashboard blocks the main thread
 * for 3-4 seconds while all dashboard components are evaluated.
 *
 * With lazy loading:
 * 1. Navigation starts immediately
 * 2. Skeleton shows while chunks load in background
 * 3. Dashboard renders when ready
 */

import type { ReactElement } from 'react';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AuthLoadingSkeleton } from '@/features/auth/components/AuthLoadingSkeleton';

// Lazy load DashboardPage to prevent blocking navigation
// IMPORTANT: Import directly from component file, NOT from barrel index
// The barrel file (index.ts) exports ALL dashboards which triggers eager evaluation
const DashboardPage = lazy(() =>
  import('@/features/dashboard/components/DashboardPage').then(m => ({ default: m.DashboardPage }))
);

/**
 * Dashboard route wrapper component with lazy loading.
 *
 * Uses React.lazy + Suspense to defer heavy dashboard loading.
 * The skeleton shows immediately while dashboard chunks load in background,
 * providing instant visual feedback on navigation.
 *
 * @component
 */
function DashboardRoute(): ReactElement {
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    if (!hasLoggedRef.current) {
      hasLoggedRef.current = true;
          }
  }, []);

  return (
    <Suspense fallback={<AuthLoadingSkeleton />}>
      <DashboardPage />
    </Suspense>
  );
}

/**
 * Dashboard route configuration.
 *
 * Defines the dashboard route ('/dashboard').
 * Authentication and onboarding checks are handled by the parent _authenticated route.
 *
 * IMPORTANT: The loader function is required to trigger TanStack Router's
 * pending state during navigation. Without it, defaultPendingComponent
 * will never show. See: https://github.com/TanStack/router/issues/3556
 */
// TEMPORARY TEST: Removed gcTime: 0 and loader to test with caching
// TODO: Revert after testing
export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardRoute,
});